import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "./api/useGetWorkerStripeAccount";
import { type WorkplaceShift } from "./api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { type ShiftAction } from "./components/ShiftActionItem";
import { OmnisearchDialog } from "./OmnisearchDialog";
import { useOmnisearchKeyboardShortcut } from "./useOmnisearchKeyboardShortcut";

interface OmnisearchContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const OmnisearchContext = createContext<OmnisearchContextValue | undefined>(undefined);

interface OmnisearchProviderProps {
  children?: ReactNode;
  onSelectAgent: (agent: Agent) => void;
  onSelectFacility: (facility: Facility) => void;
  onSelectShift: (shift: ShiftSearchResult) => void;
  onSelectShiftAction: (shift: ShiftSearchResult, action: ShiftAction) => void;
  onSelectAgentShift: (workerShift: WorkerShift) => void;
  onSelectAgentStripe: (stripeAccount: WorkerStripeAccount) => void;
  onSelectWorkplaceShift: (workplaceShift: WorkplaceShift) => void;
}

export function OmnisearchProvider(props: OmnisearchProviderProps) {
  const {
    children,
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
    onSelectAgentStripe,
    onSelectWorkplaceShift,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOmnisearchKeyboardShortcut({
    onOpen: open,
    onClose: close,
    isOpen,
  });

  const contextValue: OmnisearchContextValue = useMemo(
    () => ({
      isOpen,
      open,
      close,
    }),
    [isOpen, open, close]
  );

  return (
    <OmnisearchContext.Provider value={contextValue}>
      {children}
      <OmnisearchDialog
        open={isOpen}
        onClose={close}
        onSelectAgent={onSelectAgent}
        onSelectFacility={onSelectFacility}
        onSelectShift={onSelectShift}
        onSelectShiftAction={onSelectShiftAction}
        onSelectAgentShift={onSelectAgentShift}
        onSelectAgentStripe={onSelectAgentStripe}
        onSelectWorkplaceShift={onSelectWorkplaceShift}
      />
    </OmnisearchContext.Provider>
  );
}

export function useOmnisearch(): OmnisearchContextValue {
  const context = useContext(OmnisearchContext);
  if (!context) {
    throw new Error("useOmnisearch must be used within an OmnisearchProvider");
  }

  return context;
}
